import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Field, FormError } from '@/components/form';
import { PageHeader } from '@/components/page';
import { DetailsEditor, filledDetails, newRow, type DetailRow } from '@/features/register/details-editor';
import { api, ApiError, errorMessage } from '@/lib/api';
import { can, meQuery, useMe } from '@/lib/auth';
import { ENTRY_TYPE_LABEL } from '@/lib/labels';
import type { EntryType, FraudCase, SkippedDetail } from '@/lib/types';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/cases/new')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me || !can.record(me)) throw redirect({ to: '/cases', search: {} });
  },
  component: NewCasePage,
});

type Source = 'APPLICATION' | 'MANUAL';

function NewCasePage() {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const admin = can.admin(me);
  const [source, setSource] = useState<Source>('APPLICATION');
  const [reference, setReference] = useState('');
  const [brand, setBrand] = useState('');
  const [branch, setBranch] = useState('');
  const [entryType, setEntryType] = useState<EntryType>('CONFIRMED_INVOLVED');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [rows, setRows] = useState<DetailRow[]>([newRow('APPLICANT', 'NAME'), newRow('APPLICANT', 'EMAIL'), newRow('APPLICANT', 'PHONE')]);

  const create = useMutation({
    mutationFn: () =>
      api.post<FraudCase & { skipped: SkippedDetail[] }>('/v1/cases', {
        source,
        sourceReference: source === 'APPLICATION' ? reference.trim() : undefined,
        brand: brand.trim() || undefined,
        branch: branch.trim() || undefined,
        entryType,
        reason: reason.trim() || undefined,
        evidence: evidence.trim() || undefined,
        details: filledDetails(rows),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['cases'] });
      await queryClient.invalidateQueries({ queryKey: ['entries'] });
      toast.success(`Recorded ${created.entries.length} ${created.entries.length === 1 ? 'entry' : 'entries'}`);
      if (created.skipped.length) {
        toast.warning(`${created.skipped.length} detail(s) not stored`, {
          description: created.skipped.map((s) => `${s.value}: ${s.reason}`).join('\n'),
          duration: 12_000,
        });
      }
      await navigate({ to: '/cases/$caseId', params: { caseId: created.id } });
    },
  });

  const details = filledDetails(rows);
  const valid =
    details.length > 0 &&
    (source === 'APPLICATION' ? reference.trim().length > 0 : reason.trim().length > 0 && evidence.trim().length > 0);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (valid) create.mutate();
  }

  const skipped = create.error instanceof ApiError ? ((create.error.body.skipped as SkippedDetail[] | undefined) ?? []) : [];

  return (
    <div className="max-w-3xl">
      <PageHeader
        back={
          <Link to="/cases" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Cases
          </Link>
        }
        title={admin ? 'New register entry' : 'Record fraud'}
        description="Add the details seen in a fraudulent application, or (with the fraud admin permission) a warning with no application behind it."
      />
      <form onSubmit={submit} className="grid gap-6 rounded-xl border bg-card p-5 md:p-6" noValidate>
        <FormError message={create.isError ? errorMessage(create.error) : null} />
        {skipped.length ? (
          <ul className="-mt-3 list-disc pl-6 text-sm text-destructive">
            {skipped.map((s, i) => (
              <li key={i}>
                {s.value || '(empty)'}: {s.reason}
              </li>
            ))}
          </ul>
        ) : null}

        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-medium">What is this based on?</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {(
              [
                { value: 'APPLICATION', title: 'An application', body: 'Fraud found in an application you processed.', allowed: true },
                { value: 'MANUAL', title: 'A warning (no application)', body: 'E.g. a group alert about an email or employer. Needs the fraud admin permission.', allowed: admin },
              ] as const
            ).map((o) => (
              <label
                key={o.value}
                className={cn(
                  'flex cursor-pointer gap-3 rounded-lg border p-3 has-[:checked]:border-primary has-[:checked]:bg-accent',
                  !o.allowed && 'cursor-not-allowed opacity-60',
                )}
              >
                <input type="radio" name="source" value={o.value} checked={source === o.value} disabled={!o.allowed} onChange={() => setSource(o.value)} className="mt-1 accent-[var(--primary)]" />
                <span>
                  <span className="block text-sm font-medium">{o.title}</span>
                  <span className="block text-[13px] text-muted-foreground">{o.body}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {source === 'APPLICATION' ? (
          <div className="grid gap-4 sm:grid-cols-3">
            <Field id="reference" label="Application reference">
              <Input id="reference" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} required />
            </Field>
            <Field id="brand" label="Brand (optional)">
              <Input id="brand" value={brand} onChange={(e) => setBrand(e.target.value)} maxLength={200} />
            </Field>
            <Field id="branch" label="Branch (optional)">
              <Input id="branch" value={branch} onChange={(e) => setBranch(e.target.value)} maxLength={200} />
            </Field>
          </div>
        ) : null}

        <div className="grid gap-1.5 sm:max-w-xs">
          <Label id="entry-type-label">Entry type</Label>
          <Select value={entryType} onValueChange={(v) => setEntryType(v as EntryType)}>
            <SelectTrigger aria-labelledby="entry-type-label" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CONFIRMED_INVOLVED">{ENTRY_TYPE_LABEL.CONFIRMED_INVOLVED}</SelectItem>
              <SelectItem value="DETAILS_USED">{ENTRY_TYPE_LABEL.DETAILS_USED}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[13px] text-muted-foreground">“Details used” is for details that belong to someone else, such as a real employer whose name was borrowed.</p>
        </div>

        <DetailsEditor rows={rows} onChange={setRows} />

        <div className="grid gap-4">
          <Field id="reason" label={source === 'MANUAL' ? 'Reason' : 'Notes (optional)'}>
            <Textarea id="reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={2000} />
          </Field>
          <Field id="evidence" label={source === 'MANUAL' ? 'Evidence' : 'Evidence (optional)'} hint="Where the information came from. Attach documents after saving.">
            <Textarea id="evidence" value={evidence} onChange={(e) => setEvidence(e.target.value)} rows={2} maxLength={5000} />
          </Field>
        </div>

        <div className="flex justify-end gap-2 border-t pt-5">
          <Button type="button" variant="outline" asChild>
            <Link to="/cases">Cancel</Link>
          </Button>
          <Button type="submit" disabled={!valid || create.isPending || !can.write(me)}>
            {create.isPending ? 'Saving…' : 'Save to register'}
          </Button>
        </div>
      </form>
    </div>
  );
}
